/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import { useAppSelector } from '../../reducers';
import { getCurrentUser } from '../../selectors/user';
import G17Client from '../../services/G17Client';
import { useDispatch } from 'react-redux';
import { addSiteAlert } from '../../slice/siteAlertsSlice';
import { UtrvComment } from '../../types/universalTrackerValue';
import { UserMin } from '../../constants/users';
import { naturalSort } from '../../utils';
import Comment from './comment';
import AddComment from './addComment';
import { SuggestionDataItem } from 'react-mentions';
import LoadingPlaceholder from '../LoaderContainer/LoadingPlaceholder';
import './styles.scss';
import { QUESTION } from '../../constants/terminology';

interface UtrvCommentsProps {
  utrvId: string;
  toggle: () => void;
  commentId?: string;
}

type LocalUser = Pick<UserMin, '_id' | 'firstName' | 'surname'>;

const UtrvComments = ({ utrvId, toggle, commentId }: UtrvCommentsProps) => {
  const dispatch = useDispatch();

  const [comments, setComments] = useState<UtrvComment[]>([]);
  const [users, setUsers] = useState<LocalUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const user = useAppSelector(getCurrentUser);
  const pageSizeIncrement = 3;
  const [pageSize, setPageSize] = useState(pageSizeIncrement);

  const hydrateUser = useCallback((item: UtrvComment, users: LocalUser[]) => {
    const user = users.find(u => u._id === item.userId);
    if (user) {
      return {
        _id: user._id,
        name: `${user.firstName} ${user.surname}`
      }
    }
    return {
      _id: item.userId,
      name: '-'
    };
  }, []);

  const handleReload = useCallback(() => {
    setIsLoading(true);
    G17Client.getUtrvComments(utrvId)
      .then(data => {
        if (data) {
          setComments(
            data.items
              .sort((a, b) => naturalSort(b.created, a.created))
              .map(item => ({
                ...item,
                user: hydrateUser(item, data.users)
              }))
          );
          setUsers(data.users);
        }
        setIsLoading(false);
      })
      .catch((e: Error) => {
        dispatch(addSiteAlert({
          content: `There was a problem fetching the comments for this ${QUESTION.SINGULAR}. Please refresh and try again.`
        }));
        setIsLoading(false);
      })
  }, [setComments, hydrateUser, dispatch, utrvId]);

  React.useEffect(() => {
    handleReload();
  }, [utrvId, handleReload]);

  const mentionUsers: SuggestionDataItem[] = useMemo(() => users.map(user => ({ id: user._id, display: `${user.firstName} ${user.surname}` })), [users]);

  if (!user) {
    return null;
  }

  const visibleComments = commentId ? comments : comments.slice(0, pageSize);

  return <div className='utrv-comments'>
    <AddComment users={mentionUsers} toggle={toggle} utrvId={utrvId} user={user} handleReload={handleReload} />
    <LoadingPlaceholder isLoading={isLoading} height={32} >
      <div className='p-2 text-ThemeTextPlaceholder text-right'>
        {comments.length} comments
      </div>
    </LoadingPlaceholder>
    <LoadingPlaceholder isLoading={isLoading} height={150}>
    {visibleComments.map((comment, i) => (
      <Comment
        isScrolledTo={comment._id === commentId}
        users={mentionUsers}
        key={`comment_${utrvId}-${comment._id}-${i}`}
        comment={comment}
        utrvId={utrvId}
        user={user}
        handleReload={handleReload}
      />
    ))}
    </LoadingPlaceholder>
    <LoadingPlaceholder isLoading={isLoading} height={32}>
      {comments.length > pageSize && visibleComments.length !== comments.length ? (
        <div className='mt-3 text-center'>
          <Button size='sm' onClick={() => setPageSize(pageSize + pageSizeIncrement)}>Load {Math.min(pageSizeIncrement, comments.length - pageSize)} More</Button>
          <div className='mt-3 text-ThemeTextMedium'>{comments.length - pageSize} previous comments</div>
        </div>
      ) : null}
    </LoadingPlaceholder>
  </div>
};

export default UtrvComments;
